import { useParams } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useTenantByIdQuery, useTenantLanguages } from '@/entities/tenant'
import { useTourByIdQuery } from '@/entities/tour'
import { useMediaUploadUi } from '@/shared/hooks/use-media-upload-ui'
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard'
import { resolveDefaultLanguages } from '@/shared/utils/language-utils'
import { useTourFormActions } from './use-tour-form-actions'
import { useTourFormOptions } from './use-tour-form-options'
import { useTourFormState } from './use-tour-form-state'
import { useTourPoiLanguage } from './use-tour-poi-language'
import { useTourFormSteps } from './use-tour-form-steps'
import { useTourPois } from './use-tour-pois'

/**
 * Everything the tour create/edit page needs, composed from focused hooks:
 * form values + dirty tracking, destination/geo options, POI picker, media UI and save actions.
 */
export function useTourForm() {
  const params = useParams({ strict: false })
  const tenantId = params.tenantId ?? ''
  const { tourId } = params
  const isEdit = Boolean(tourId)

  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const { data: tenantResponse } = useTenantByIdQuery(tenantId)
  const { data: tourResponse, isLoading: isTourLoading } = useTourByIdQuery(tourId ?? '')
  const isFormReady = !isTourLoading && tenantLanguagesQuery.isFetched

  const state = useTourFormState({ isEdit, tourId, tenantId, tour: tourResponse?.data })
  const { basicInfo, selectedPois } = state.formState

  const tenantLanguageItems = useMemo(
    () => tenantLanguagesQuery.items ?? [],
    [tenantLanguagesQuery.items],
  )
  const languages = useMemo(
    () => resolveDefaultLanguages(tenantLanguageItems),
    [tenantLanguageItems],
  )
  const poiLanguage = useTourPoiLanguage(tenantLanguageItems)

  const options = useTourFormOptions({
    basicInfo,
    selectedPois,
    languageCode: poiLanguage.effectiveLanguageCode,
    updateBasicInfo: state.updateBasicInfo,
  })
  const pois = useTourPois({
    tenantId,
    tenantDestinationId: basicInfo.tenantDestinationId,
    languageCode: poiLanguage.effectiveLanguageCode,
    isEdit,
    tourId,
    selectedPois,
    updateSelectedPois: state.updateSelectedPois,
    serverPoisRef: state.serverPoisRef,
    commitPoisBaseline: state.commitBaseline.pois,
  })
  const media = useMediaUploadUi()
  const steps = useTourFormSteps({
    isEdit,
    formState: state.formState,
    poiMap: pois.poiMap,
    isMediaUploading: media.isMediaUploading,
  })
  const unsavedChanges = useUnsavedChangesGuard(state.hasUnsavedChanges)
  const actions = useTourFormActions({
    tenantId,
    tourId,
    isEdit,
    formState: state.formState,
    poiMap: pois.poiMap,
    isActive: state.isActive,
    isMediaUploading: media.isMediaUploading,
    clearCurrentJobs: state.clearCurrentJobs,
    allowNextNavigation: unsavedChanges.allowNextNavigation,
    commitBaseline: state.commitBaseline,
  })

  return {
    tenantId,
    isEdit,
    isFormReady,
    tenantName: tenantResponse?.data?.name ?? tenantId,
    languages,
    poiLanguage,
    state,
    options,
    pois,
    media,
    actions,
    steps,
    unsavedChangesDialog: unsavedChanges.dialog,
  }
}

export type TourFormController = ReturnType<typeof useTourForm>
